import { env } from "env";
import { jwtDecode } from "jwt-decode";
import {
  BehaviorSubject,
  filter,
  finalize,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import { authAct } from "./authApi";
import { initService } from "helpers/initService";

const GUEST_PATHS = ["/auth", "/invites", "/onboarding"];

// const LOGIN_URL = import.meta.envREACT_APP_ACCESS_TOKEN
const TOKEN_REFRESH_BUFFER = 10 * 60 * 1000; // 10 minutes
interface AccessToken {
  auth_time: number;
  client_id: string;
  event_id: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  scope: string;
  sub: string;
  token_use: string;
  username: string;
  version: 2;
}
export interface AuthState {
  accessToken: AccessToken;
  source: "authServer" | "cache";
}

export type AuthTokens = {
  accessTokenString: string;
};
class AuthService {
  private readonly _accessTokenString$ = new BehaviorSubject<string | null>(null);
  private readonly _accessToken$ = new BehaviorSubject<AccessToken | null>(null);
  public readonly accessTokensLoaded$ = this._accessToken$.pipe(map(() => !!this._accessToken$.value));

  private readonly _state$ = new ReplaySubject<AuthState | null>(1);
  private readonly refreshObservable$ = new BehaviorSubject<AuthTokens | "loading" | null>(null);
  private readonly _initCalled$ = new BehaviorSubject<boolean>(false);
  public readonly state$ = this._state$.asObservable();

  constructor() {
    const hash = window.location.hash;
    if (hash.length && hash.indexOf("access_token=") >= 0) {
      const params = new URLSearchParams(window.location.hash || "");
      const accessToken = (params.get("access_token") || params.get("#access_token"))!;
      this.setCredentials({ accessToken });
    }
    // FYI, this does not pick up storage pages from current tab. only other tabs.
    window.addEventListener("storage", (event) => {
      if (event.key === "access_token" && event.newValue !== this._accessTokenString$.value) {
        event.newValue
          ? this.setCredentials(
              { accessToken: event.newValue },
              !window.location.pathname.startsWith("/auth"),
            )
          : this.logout();
      } else if (!event.key && !event.newValue) {
        this.logout();
      }
    });
  }

  public setCredentials = (
    props: {
      accessToken: string;
      redirectTo?: string;
    },
    noRedirect: boolean = false,
  ) => {
    const { accessToken, redirectTo } = props;
    this._initCalled$.next(true);
    this._accessTokenString$.next(accessToken);
    this._accessToken$.next(jwtDecode(accessToken));
    if (localStorage.getItem("access_token") !== accessToken && accessToken)
      localStorage.setItem("access_token", accessToken);

    !noRedirect && window.history.replaceState({}, document.title, window.location.pathname);
    this._state$.next({
      accessToken: jwtDecode(accessToken),
      source: "authServer",
    });
    if (noRedirect) return;
    const last_url =
      (sessionStorage.getItem && sessionStorage.getItem("last_url")) ??
      (localStorage?.getItem && localStorage.getItem("last_url"));
    localStorage.removeItem && localStorage.removeItem("last_url");
    sessionStorage.removeItem && sessionStorage.removeItem("last_url");
    if (!redirectTo && last_url && !GUEST_PATHS.some((path) => last_url.startsWith(path))) {
      window.location.href = last_url ?? "/";
    }
    window.location.href = redirectTo ?? "/";
  };

  public redirectToLogin = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)?.analytics?.reset && (window as any)?.analytics?.reset();
    if (!GUEST_PATHS.some((path) => window.location.pathname.startsWith(path))) {
      try {
        localStorage.setItem("last_url", window.location.pathname + window.location.search);
        sessionStorage.setItem("last_url", window.location.pathname + window.location.search);
      } catch {
        // meh... safarIE does not like localstorage sometimes. what are you going to do
      }
      window.location.href = "/auth";
    }
  };

  public getTokens = (): Observable<AuthTokens | null> => {
    if (!this._initCalled$.value) {
      this._initCalled$.next(true);
      const accessTokenString = localStorage?.getItem?.("access_token") ?? this._accessTokenString$.value;
      if (accessTokenString) {
        const accessToken = jwtDecode(accessTokenString);
        this._accessTokenString$.next(accessTokenString);
        this._accessToken$.next(accessToken as AccessToken);
        this._state$.next({
          accessToken: accessToken as never,
          source: "cache",
        });
      }
    }

    if (this.isExpiredToken()) {
      return this.refresh();
    }

    if (this.isNearExpiry()) {
      this.refresh().subscribe();
    }

    return this._accessTokenString$.value
      ? of({
          accessTokenString: this._accessTokenString$.value,
        })
      : of(null);
  };

  public readonly refresh = (): Observable<AuthTokens | null> => {
    if (env.VITE_CAN_USE_REFRESH_TOKENS !== "true") {
      return of(null);
    }
    if (GUEST_PATHS.some((path) => window.location.pathname.startsWith(path))) {
      return of(null);
    }

    if (!this.refreshObservable$?.value) {
      this.refreshObservable$.next("loading");
      return authAct("actLoginRefreshLogin", {})
        .catchClientError("AuthFailureDto", () => {
          this.redirectToLogin();
        })
        .catchErrorByStatus(422, () => {
          this.redirectToLogin();
        })
        .$()
        .pipe(
          switchMap((res) => {
            if (!res?.accessToken) {
              return of(null);
            }
            const authTokens: AuthTokens = {
              accessTokenString: res.accessToken,
            };
            return of(authTokens);
          }),
          tap((authTokens) => {
            if (authTokens) {
              this.setCredentials({ accessToken: authTokens.accessTokenString }, true);
              this.refreshObservable$.next(authTokens);
            }
          }),
          finalize(() => {
            this.refreshObservable$.next(null as unknown as AuthTokens);
          }),
          shareReplay(1),
        );
    }
    return this.refreshObservable$.pipe(filter((value) => value !== "loading")) ?? this.refresh();
  };

  private readonly isExpiredToken = () => {
    if (!this._accessTokenString$.value || !this._accessToken$.value) {
      return true;
    }
    return this._accessToken$.value.exp * 1000 < Date.now();
  };

  private readonly isNearExpiry = (): boolean => {
    if (!this._accessTokenString$.value || !this._accessToken$.value) {
      return true;
    }
    return this._accessToken$.value.exp * 1000 - Date.now() < TOKEN_REFRESH_BUFFER;
  };

  public logout = () => {
    this._accessTokenString$.next(null);
    this._accessToken$.next(null);
    this._state$.next(null);
    const clientId = localStorage.getItem("client_id");
    if (localStorage.getItem("access_token")) {
      localStorage.clear();
      clientId && localStorage.setItem("client_id", clientId); // preserve client id after logout for next login.
    }
    sessionStorage.setItem &&
      sessionStorage.setItem("last_url", window.location.pathname + window.location.search);
    window?.analytics?.track && window?.analytics?.track("Logout");
    window?.analytics?.reset && window?.analytics?.reset();
    authAct("actLoginLogout", {}).$().subscribe();
    setTimeout(() => {
      window.location.href = window.location.pathname.startsWith("/auth") ? window.location.href : "/auth";
    }, 500);
  };
}

let authService: Readonly<AuthService> | null = null;

export const getAuthService = () => {
  if (!authService) {
    authService = initService(() => new AuthService());
  }
  return authService;
};
